import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import { ESC, SPACE, RETURN, UP, DOWN } from "@xo-union/constant-key-codes";
import { useSelectContext } from "../SelectContext/index.js";
import { useElementRef, useElementID } from "../ElementSync/index.js";
import useMergeRefs from "../useMergeRefs.js";
const useEventHandlerProps = () => {
  const select = useSelectContext();
  return React.useMemo(() => {
    const onKeyDown = e => {
      if (e.keyCode === SPACE || e.keyCode === RETURN || e.keyCode === UP || e.keyCode === DOWN) {
        select.open();
        e.preventDefault();
      }
      if (e.keyCode === ESC) {
        /**
         * Would prefer to use `currentTarget` but it makes testing a bit harder
         *
         * See this unresolved issue from enzyme:
         * https://github.com/airbnb/enzyme/issues/218
         */
        e.target.blur();
      }
    };
    return {
      onMouseDown: select.disabled ? noop : select.toggle,
      onKeyDown: select.disabled ? noop : onKeyDown
    };
  }, [select]);
};

/**
 * Renders the menu button for the custom select. This is the primary interactive component to
 * initiate usage of the Select
 */
const MenuButton = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    as: ComponentToRender,
    ...props
  } = _ref;
  const select = useSelectContext();
  const elementSyncRef = useElementRef('MenuButton');
  const labelID = useElementID('Label');
  const focusedOptionID = useElementID('FocusedOption');
  const listBoxID = useElementID('ListBox');
  const eventHandlerProps = useEventHandlerProps();
  return /*#__PURE__*/React.createElement(ComponentToRender, _extends({
    ref: useMergeRefs([elementSyncRef, ref]),
    tabIndex: select.disabled ? '-1' : '0',
    disabled: select.disabled
  }, eventHandlerProps, {
    /* Aria props */
    "aria-labelledby": `${labelID} ${focusedOptionID}`,
    "aria-haspopup": "listbox",
    "aria-controls": listBoxID,
    "aria-expanded": select.isOpen,
    "aria-disabled": select.disabled
  }, props));
});
process.env.NODE_ENV !== "production" ? MenuButton.propTypes = {
  /**
   * Underlying component to render
   */
  as: PropTypes.elementType,
  /**
   * Override optino ID
   */
  id: PropTypes.string
} : void 0;
MenuButton.defaultProps = {
  as: 'button'
};
export default MenuButton;